/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  journal: {
    all: ["journal"] as const,
    /** Entries for a specific day (Today / calendar day). */
    byDate: (userId: string | undefined, dateKey: string) =>
      ["journal", "byDate", userId, dateKey] as const,
    /** Entries in a [start, end] range (export). */
    range: (userId: string | undefined, startKey: string, endKey: string) =>
      ["journal", "range", userId, startKey, endKey] as const,
    /** Days with an entry (streaks + calendar dots). */
    days: (userId: string | undefined) => ["journal", "days", userId] as const,
    /** Details of an entry. */
    detail: (entryId: string | undefined) => ["journal", "detail", entryId] as const,
    /** Local full-text search. */
    search: (userId: string | undefined, term: string) =>
      ["journal", "search", userId, term] as const,
    /** Combined filter (text + mood + tag + range). */
    filter: (userId: string | undefined, signature: string) =>
      ["journal", "filter", userId, signature] as const,
    /** Mood distribution (analytics). */
    moodDistribution: (userId: string | undefined) =>
      ["journal", "moodDistribution", userId] as const,
    /** Monthly mood time series (analytics). */
    monthlyMood: (userId: string | undefined, limit: number) =>
      ["journal", "monthlyMood", userId, limit] as const,
    /** Raw mood rows (input to the weekly series). */
    moodEntries: (userId: string | undefined) => ["journal", "moodEntries", userId] as const,
    /** "On this day" (same MM-DD in previous years). */
    onThisDay: (userId: string | undefined, todayKey: string) =>
      ["journal", "onThisDay", userId, todayKey] as const,
    /** Favorite entries. */
    favorites: (userId: string | undefined) => ["journal", "favorites", userId] as const,
    /** User tags. */
    tags: (userId: string | undefined) => ["journal", "tags", userId] as const,
    /** Tags for an entry. */
    entryTags: (entryId: string | undefined) => ["journal", "entryTags", entryId] as const,
    /** Entry→tags map for the entire user (lists). */
    allEntryTags: (userId: string | undefined) => ["journal", "allEntryTags", userId] as const,
    /** Entries with a tag. */
    byTag: (userId: string | undefined, tagId: string | undefined) =>
      ["journal", "byTag", userId, tagId] as const,
    /** Photos for an entry. */
    photos: (entryId: string | undefined) => ["journal", "photos", entryId] as const,
    /** Photos for all of the user's entries. */
    allPhotos: (userId: string | undefined) => ["journal", "allPhotos", userId] as const,
    /** Global totals (entries, days, words). */
    totals: (userId: string | undefined) => ["journal", "totals", userId] as const,
    /** Monthly statistics. */
    monthly: (userId: string | undefined, limit: number) =>
      ["journal", "monthly", userId, limit] as const,
    /** Version history for an entry (L4). */
    versions: (entryId: string | undefined) => ["journal", "versions", entryId] as const,
    /** Trash: entries with deleted_at (L2). */
    trash: (userId: string | undefined) => ["journal", "trash", userId] as const,
    /** Local activity feed (activity_recent, L3). */
    activity: (userId: string | undefined) => ["journal", "activity", userId] as const,
    /** Activity history for a specific entity (L3). */
    activityEntity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["journal", "activity", "entity", userId, entityType, entityId] as const,
  },
  activity: {
    all: ["activity"] as const,
    /**
     * Local feed (activity_recent). Includes userId: isolates cache between accounts.
     * `filter` are the app's own dimensions (see `ActivityFeedFilter`).
     */
    feed: (userId: string | undefined, filter: Record<string, unknown> = {}) =>
      ["activity", "feed", userId, filter] as const,
    /** Local history for a specific entity. */
    entity: (userId: string | undefined, entityType: string, entityId: string | undefined) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Are there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + gallery SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * Devices with an open session. Prefix WITHOUT a suffix: this is the key
     * invalidated after revoking a session (P23/CC-60 · P25). It used to be an inline
     * array (`["active-sessions", id]`) inside the profile section, so the
     * sign-out machine —the component that knows revocation succeeded— had no
     * way to invalidate it, and the list kept showing a device that was no
     * longer there.
     */
    activeSessions: () => ["auth", "activeSessions"] as const,
    /** The list as requested by the screen, anchored to the current session. */
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId] as const,
  },
};
